import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Table, Badge, Button, Form, Spinner } from "react-bootstrap";
import { getDataAPI, postDataAPI } from "../../../services/apiWrapper";
import { ALERT } from "../../../store/actions/actionTypes";
import { exportListExcel, printListPdf } from "../../../utils/listExport";

/**
 * Council > Placement > Verifications. Review the joining proof an employer
 * submitted and mark the placement NSVEC-verified. Does not change selection.
 */
const FILE_BASE = "https://nsvec.com/api/public/";
const token = () => localStorage.getItem("adminToken");
const badge = (s) => {
  const v = { verified: "success", employer_confirmed: "info" }[s] || "warning";
  return <Badge variant={v}>{(s || "").replace(/_/g, " ")}</Badge>;
};
const doc = (path, label) => (path ? <a href={FILE_BASE + path} target="_blank" rel="noopener noreferrer">{label}</a> : null);

export default function Placements() {
  const dispatch = useDispatch();
  const alert = (payload) => dispatch({ type: ALERT, payload });

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async (toPage = page) => {
    setBusy(true);
    try {
      const res = await dispatch(getDataAPI(`admin/placement/placements?status=${status}&search=${encodeURIComponent(search)}&page=${toPage}`, token()));
      const d = res.data.data || {};
      setRows(d.data || []);
      setMeta({ current_page: d.current_page || 1, last_page: d.last_page || 1, total: d.total || 0 });
      setPage(d.current_page || 1);
    } catch (e) { alert({ error: "Could not load placements." }); }
    setBusy(false);
  };
  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [status]);

  const verify = async (id) => {
    if (!window.confirm("Mark this placement NSVEC-verified?")) return;
    setBusy(true);
    try {
      await dispatch(postDataAPI(`admin/placement/placements/${id}/verify`, {}, token()));
      alert({ success: "Placement verified." });
      load();
    } catch (e) { alert({ error: "Could not verify." }); }
    setBusy(false);
  };

  const columns = [
    { key: "candidate", label: "Candidate" },
    { key: "roll_number", label: "Roll" },
    { key: "org_name", label: "Employer" },
    { key: (r) => (r.status || "").replace(/_/g, " "), label: "Status" },
  ];
  const fetchAllRows = async () => {
    const res = await dispatch(getDataAPI(`admin/placement/placements?status=${status}&search=${encodeURIComponent(search)}&per_page=100000`, token()));
    return (res.data.data && res.data.data.data) || [];
  };
  const exportExcel = async () => { try { exportListExcel(columns, await fetchAllRows(), "placement-verifications"); } catch (e) { alert({ error: "Could not export." }); } };
  const exportPdf = async () => { try { printListPdf("Placement — Verifications", columns, await fetchAllRows()); } catch (e) { alert({ error: "Could not export." }); } };

  return (
    <div className="container-fluid py-4">
      <h4 className="mb-3">Placement &mdash; Verifications</h4>
      <div className="d-flex flex-wrap align-items-end mb-3" style={{ gap: 12 }}>
        <Form.Group className="mb-0">
          <Form.Label className="small mb-1">Status</Form.Label>
          <Form.Control as="select" value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 180 }}>
            <option value="all">All</option>
            <option value="joining_submitted">Joining submitted</option>
            <option value="employer_confirmed">Employer confirmed</option>
            <option value="verified">Verified</option>
          </Form.Control>
        </Form.Group>
        <Form.Group className="mb-0">
          <Form.Label className="small mb-1">Search</Form.Label>
          <Form.Control value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(1)} placeholder="Candidate / roll" style={{ width: 220 }} />
        </Form.Group>
        <Button variant="primary" onClick={() => load(1)} disabled={busy}>Search</Button>
        <div className="ml-auto d-flex" style={{ gap: 8 }}>
          <Button variant="outline-success" size="sm" onClick={exportExcel}>Excel</Button>
          <Button variant="outline-secondary" size="sm" onClick={exportPdf}>PDF</Button>
        </div>
      </div>

      <Table bordered hover responsive size="sm">
        <thead className="thead-light">
          <tr><th>#</th><th>Candidate</th><th>Roll</th><th>Employer</th><th>Documents</th><th>Status</th><th>Action</th></tr>
        </thead>
        <tbody>
          {busy && <tr><td colSpan={7} className="text-center py-3"><Spinner animation="border" size="sm" /></td></tr>}
          {!busy && rows.length === 0 && <tr><td colSpan={7} className="text-center text-muted py-3">No placements.</td></tr>}
          {!busy && rows.map((r, i) => (
            <tr key={r.id}>
              <td>{(meta.current_page - 1) * 25 + i + 1}</td>
              <td>{r.candidate}</td>
              <td>{r.roll_number}</td>
              <td>{r.org_name}</td>
              <td style={{ whiteSpace: "nowrap" }}>
                {[doc(r.offer_letter, "Offer"), doc(r.appointment_letter, "Appointment"), doc(r.joining_proof, "Joining"), doc(r.id_card, "ID")]
                  .filter(Boolean).reduce((acc, el, idx) => acc.length ? [...acc, <span key={"s" + idx}> | </span>, el] : [el], [])}
                {!r.offer_letter && !r.appointment_letter && !r.joining_proof && !r.id_card && <span className="text-muted">-</span>}
              </td>
              <td>{badge(r.status)}</td>
              <td>
                {r.status !== "verified"
                  ? <Button size="sm" variant="success" disabled={busy} onClick={() => verify(r.id)}>Verify</Button>
                  : <span className="text-success small">Verified</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="d-flex align-items-center" style={{ gap: 10 }}>
        <small className="text-muted">Page {meta.current_page} of {meta.last_page} &bull; {meta.total} total</small>
        <div className="ml-auto">
          <Button size="sm" variant="light" disabled={busy || page <= 1} onClick={() => load(page - 1)}>Prev</Button>{" "}
          <Button size="sm" variant="light" disabled={busy || page >= meta.last_page} onClick={() => load(page + 1)}>Next</Button>
        </div>
      </div>
    </div>
  );
}
